import { useEffect, useState } from 'react';
import type { Rrpp } from '../../../api/ops/listas';
import { ErrorApi } from '../../../api/problema';
import { Boton } from '../../../componentes/Boton';
import { Interruptor } from '../../../componentes/Interruptor';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { Ventana } from '../../../componentes/Ventana';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { dinero } from '../../../logica/dinero';
import { usePermisos } from '../../../permisos/permisos';
import { rutaDeApartado } from '../../apartados';
import { Bloque, Botonera, Botones, Caja, Copiar, Dato, Fila, Vacia } from '../comun';
import { useAvisarError, useCarga } from '../uso';
import { BASE, comoSeCalcula, cuandoEs, diaHora, nombreRrpp, opsDe, PERMISOS, sinProtocolo } from '../textos';
import type { ContextoRrpp } from './ApartadoRrpp';
import { EnlaceDeRrpp } from './Enlaces';

/**
 * Ficha del RRPP (10.10): Datos (código de venta y su enlace de venta de entradas, activar o desactivar y regenerar el
 * código con `updatePromoter` e `If-Match`), Enlaces (uno por evento y el fijo opcional, `createPromoterFixedLink`),
 * Comisión (lo pendiente, su tarifa y las comisiones por liquidar) y «Lo que ha hecho» (`listPromoterAttributions` con
 * `reference` y `label`, G507, G788). Nombre de `display_name` (G788).
 */
const TIPO_VENTA = { guest: 'Invitado', order: 'Entradas', reservation: 'Reservado' } as const;

export function FichaRrpp({ ctx, rrppId }: { ctx: ContextoRrpp; rrppId: string }) {
    const { props, eventos } = ctx;
    const { negocio, movil, soloLectura, membresia, navegar } = props;
    const ops = opsDe(props.ops);
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const avisarError = useAvisarError();
    const [pestana, setPestana] = useState(0);
    const [leido, setLeido] = useState<{ p: Rrpp; etag: string } | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const [codigo, setCodigo] = useState(false);
    const [vuelta, setVuelta] = useState(0);

    useEffect(() => {
        let vivo = true;
        ops.getPromoter(negocio, rrppId)
            .then((r) => { if (vivo) { setLeido({ p: r.datos, etag: r.version ?? '' }); } })
            .catch((e: unknown) => {
                if (vivo) {
                    avisarError(e, 'No se ha podido abrir el RRPP.');
                    ctx.abrir(null);
                }
            });
        return () => {
            vivo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [negocio, rrppId, vuelta]);

    const p = leido?.p;
    const miembro = p?.member_id ?? '';
    const enlaces = useCarga(pestana === 1 && miembro !== '' && permisos.puede(PERMISOS.enlacesVer) ? () => ops.listPromoterLinks(negocio, { member_id: miembro, limit: 50 }) : null, [negocio, miembro, pestana, vuelta]);
    const tarifas = useCarga(pestana === 2 ? () => ops.listCommissionRates(negocio, { level: 'promoter', promoter_id: rrppId, status: 'active' }) : null, [negocio, rrppId, pestana]);
    const pendientes = useCarga(pestana === 2 ? () => ops.listCommissions(negocio, { promoter_id: rrppId, status: 'pending', limit: 50 }) : null, [negocio, rrppId, pestana]);
    const hecho = useCarga(pestana === 3 ? () => ops.listPromoterAttributions(negocio, { promoter_id: rrppId, limit: 50 }) : null, [negocio, rrppId, pestana]);

    const puede = permisos.puede(PERMISOS.rrppGestionar) && !soloLectura;
    const nombre = nombreRrpp(p);
    const nombreEvento = (id: string) => {
        const e = eventos.find((x) => x.id === id);
        return e === undefined ? 'Evento' : `${e.name ?? ''} · ${cuandoEs(e.starts_at)}`;
    };
    const ventaUrl = `${(membresia?.public_url ?? '').replace(/\/$/, '')}/eventos?rrpp=${p?.sales_code ?? ''}`;

    const cambiar = (cambio: { status?: Rrpp['status']; regenerate_sales_code?: boolean }, texto: string) => {
        if (leido === null) {
            return;
        }
        setOcupado(true);
        ops.updatePromoter(negocio, rrppId, cambio, leido.etag)
            .then((r) => {
                setLeido({ p: r.datos, etag: r.version ?? leido.etag });
                setCodigo(false);
                avisar({ tipo: 'ok', texto });
                ctx.recargar();
            })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.estado === 412) {
                    setVuelta((v) => v + 1);
                }
                avisarError(e);
            })
            .finally(() => { setOcupado(false); });
    };

    const crearFijo = () => {
        setOcupado(true);
        ops.createPromoterFixedLink(negocio, miembro)
            .then(() => {
                avisar({ tipo: 'ok', texto: 'Enlace fijo creado. Sirve para todos sus eventos con la lista abierta.' });
                setVuelta((v) => v + 1);
            })
            .catch((e: unknown) => { avisarError(e); })
            .finally(() => { setOcupado(false); });
    };

    const lista = enlaces.datos?.data ?? [];
    const delEvento = lista.filter((e) => e.kind === 'event');
    const fijos = lista.filter((e) => e.kind === 'fixed');
    const fijoVivo = fijos.find((e) => e.status === 'active');
    const rutaLiquidar = rutaDeApartado(props, 'listas_liquidaciones', 'listas_');

    return (
        <>
            <PanelLateral
                abierto
                onCerrar={() => { ctx.abrir(null); }}
                movil={movil}
                tamano="ficha"
                titulo={nombre}
                eyebrow="RRPP"
                subtitulo={p === undefined ? '' : `Código ${p.sales_code}`}
                chip={p === undefined ? undefined : p.status === 'active' ? 'Activo' : 'Desactivado'}
                chipEstado={p?.status === 'active' ? 'confirmed' : 'neutral'}
                icono="user"
            >
                <div className="rv-ca-cuerpo">
                    <Botonera
                        etiqueta="Partes del RRPP"
                        items={[{ texto: 'Datos', icono: 'user' }, { texto: 'Enlaces', icono: 'link' }, { texto: 'Comisión', icono: 'percent' }, { texto: 'Lo que ha hecho', icono: 'chart-column' }]}
                        valor={pestana}
                        onCambio={setPestana}
                    />
                    {p === undefined ? (
                        <p className="rv-ca-nota rv-ca-nota--gris">Cargando…</p>
                    ) : (
                        <>
                            {pestana === 0 && (
                                <>
                                    <Bloque>
                                        <Dato icono="ticket" k="Código de venta" v={p.sales_code}><Copiar texto={p.sales_code} etiqueta="Copiar el código de venta" /></Dato>
                                        <Dato icono="link" k="Enlace de venta de entradas" v={sinProtocolo(ventaUrl)}><Copiar texto={ventaUrl} etiqueta="Copiar el enlace de venta" /></Dato>
                                        <Dato icono="wallet" k="Por liquidar" v={dinero(p.pending_total)} />
                                    </Bloque>
                                    <Bloque>
                                        <Interruptor
                                            etiqueta="Activo"
                                            valor={p.status === 'active'}
                                            bloqueado={!puede || ocupado}
                                            onChange={(v) => { cambiar({ status: v ? 'active' : 'inactive' }, v ? `${nombre} activado.` : `${nombre} desactivado: deja de apuntar y de sumar ventas. Lo pendiente se le liquida igual.`); }}
                                            ayuda="Desactivado no apunta invitados ni suma ventas nuevas."
                                        />
                                        {puede && (
                                            <Botones nota="Los enlaces de venta que ya haya compartido dejarán de contar para él.">
                                                <Boton tipo="secundario" icono="refresh-cw" onClick={() => { setCodigo(true); }}>Regenerar el código de venta</Boton>
                                            </Botones>
                                        )}
                                        <Caja icono="user-cog">Para quitarle el rol o sacarlo del equipo, ve a Equipo y roles.</Caja>
                                    </Bloque>
                                </>
                            )}
                            {pestana === 1 && (
                                <>
                                    <Bloque titulo="Uno por evento" intro="Para subirlos a su historia con la programación de la semana.">
                                        {enlaces.datos === null ? (
                                            <p className="rv-ca-nota rv-ca-nota--gris">Cargando…</p>
                                        ) : delEvento.length === 0 ? (
                                            <Vacia icono="instagram" titulo="Sin eventos próximos" texto="Asígnalo a un evento y tendrá su enlace." />
                                        ) : (
                                            delEvento.map((e) => (
                                                <EnlaceDeRrpp key={e.id} props={props} enlace={e} titulo={nombreEvento(e.event_id ?? '')} texto={`${String(e.signups ?? 0)} apuntados con él`} nombre={nombre} puede={permisos.puede(PERMISOS.enlacesGestionar) && !soloLectura} onCambio={() => { setVuelta((v) => v + 1); }} />
                                            ))
                                        )}
                                    </Bloque>
                                    <Bloque titulo="Enlace fijo (opcional)">
                                        {fijos.map((e) => (
                                            <EnlaceDeRrpp key={e.id} props={props} enlace={e} titulo="Su enlace fijo" texto={`Si tiene varios eventos abiertos, el chat le deja elegir con botones · ${String(e.signups ?? 0)} apuntados`} nombre={nombre} puede={permisos.puede(PERMISOS.enlacesGestionar) && !soloLectura} onCambio={() => { setVuelta((v) => v + 1); }} />
                                        ))}
                                        {fijoVivo === undefined && enlaces.datos !== null && (permisos.puede(PERMISOS.enlacesGestionar) && !soloLectura ? (
                                            <Botones nota="Un enlace que no depende del evento: para la biografía de su Instagram.">
                                                <Boton tipo="secundario" icono="plus" ocupado={ocupado} onClick={crearFijo}>Crear enlace fijo</Boton>
                                            </Botones>
                                        ) : (
                                            <Caja icono="link">No tiene enlace fijo.</Caja>
                                        ))}
                                    </Bloque>
                                </>
                            )}
                            {pestana === 2 && (
                                <>
                                    <Bloque>
                                        <Dato icono="wallet" k="Por liquidar" v={dinero(p.pending_total)} />
                                    </Bloque>
                                    <Bloque titulo="Su tarifa">
                                        {(tarifas.datos?.data ?? []).length === 0 ? (
                                            <Caja icono="store">Cobra según la tarifa de la casa, salvo donde la lista o el evento tengan la suya.</Caja>
                                        ) : (
                                            (tarifas.datos?.data ?? []).map((t) => <Fila key={t.id} baldosa="percent" titulo={`${BASE[t.basis][0]} · ${comoSeCalcula(t)[0]}`} linea={comoSeCalcula(t)[1] || 'Solo para él, en todos los eventos'} />)
                                        )}
                                    </Bloque>
                                    <Bloque titulo="Pendiente de liquidar">
                                        {pendientes.datos === null ? (
                                            <p className="rv-ca-nota rv-ca-nota--gris">Cargando…</p>
                                        ) : pendientes.datos.data.length === 0 ? (
                                            <Vacia icono="wallet" titulo="Nada pendiente" texto="Todo lo suyo está liquidado o en un borrador." />
                                        ) : (
                                            pendientes.datos.data.map((c) => (
                                                <Fila key={c.id} baldosa={c.kind === 'reversal' ? 'rotate-ccw' : BASE[c.basis][1]} titulo={c.kind === 'reversal' ? `Corrección · ${BASE[c.basis][0]}` : BASE[c.basis][0]} linea={nombreEvento(c.event_id)} derecha={dinero(c.amount)} />
                                            ))
                                        )}
                                    </Bloque>
                                    {permisos.puede(PERMISOS.liquidacionesGestionar) && !soloLectura && (p.pending_total?.amount ?? 0) > 0 && rutaLiquidar !== null && (
                                        <Botones>
                                            <Boton tipo="secundario" icono="wallet" onClick={() => { navegar(`${rutaLiquidar}&preparar=${encodeURIComponent(p.id)}`); }}>Preparar su liquidación</Boton>
                                        </Botones>
                                    )}
                                    {!permisos.puede(PERMISOS.liquidacionesGestionar) && <Caja icono="eye">Con tu rol ves sus comisiones. Las liquida el propietario o un administrador.</Caja>}
                                </>
                            )}
                            {pestana === 3 && (
                                <Bloque titulo="Lo que se le atribuye" intro="Pedidos, invitados y reservados, haya o no tarifa.">
                                    {hecho.datos === null ? (
                                        <p className="rv-ca-nota rv-ca-nota--gris">Cargando…</p>
                                    ) : hecho.datos.items.length === 0 ? (
                                        <Vacia icono="chart-column" titulo="Todavía nada" texto="Cuando apunte invitados o venda entradas con su enlace, lo verás aquí." />
                                    ) : (
                                        hecho.datos.items.map((x) => (
                                            <Fila
                                                key={`${x.source_kind}-${x.source_id}`}
                                                baldosa={x.source_kind === 'guest' ? 'user' : x.source_kind === 'order' ? 'ticket' : 'armchair'}
                                                titulo={`${TIPO_VENTA[x.source_kind]}${x.reference == null ? '' : ` · ${x.reference}`}`}
                                                linea={`${x.label ?? `${String(x.quantity)} ${x.quantity === 1 ? 'persona' : 'personas'}`} · ${nombreEvento(x.event_id)} · ${diaHora(x.created_at)}`}
                                                chip={x.status === 'cancelled' ? ['Cancelado', 'problem'] : null}
                                                derecha={x.sale_amount == null ? undefined : dinero(x.sale_amount)}
                                                apagada={x.status === 'cancelled'}
                                            />
                                        ))
                                    )}
                                </Bloque>
                            )}
                        </>
                    )}
                </div>
            </PanelLateral>
            <Ventana
                abierta={codigo}
                titulo="Regenerar el código de venta"
                texto={`El código ${p?.sales_code ?? ''} deja de atribuir ventas nuevas: las entradas compradas con los enlaces que ${nombre} ya ha compartido no contarán para su comisión.`}
                accion="Regenerar"
                accionIcono="refresh-cw"
                ocupado={ocupado}
                ocupadoTexto="Regenerando…"
                movil={movil}
                onAccion={() => { cambiar({ regenerate_sales_code: true }, 'Código nuevo listo. Lo ve en su portal.'); }}
                onCerrar={() => { if (!ocupado) { setCodigo(false); } }}
            />
        </>
    );
}
